import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Restore session from localStorage on page load.
    // access_token stays in localStorage so API request headers work.
    // refresh_token lives ONLY in an httpOnly cookie set by the backend —
    // it is never readable by JavaScript and never stored in localStorage.
    const token     = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        // Corrupt data — force re-login
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login(email, password);
    const { access_token, user } = res.data;

    // Clear stale session
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    // refresh_token is NOT stored here — the backend sets it as an
    // httpOnly cookie in the Set-Cookie response header automatically.

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('user', JSON.stringify(user));
    if (user.role) localStorage.setItem('user_role', user.role);
    setUser(user);
    return user;
  };

  // For flows that already have a valid access_token + user from a
  // non-/auth/login endpoint (registration's verify-otp, login's own
  // 2FA verify-otp step) and have already written them to localStorage
  // themselves: this is the missing piece that makes isAuthenticated
  // (and therefore PrivateRoute) true immediately, in the same render,
  // instead of only becoming true on the next full page load. Found
  // 2026-09-25: registration's own verify step wrote to localStorage
  // correctly but never called this (or login()), so isAuthenticated
  // stayed false and PrivateRoute bounced the brand-new, just-verified
  // user straight back to /login instead of onward to /onboarding.
  const setSession = (sessionUser) => {
    setUser(sessionUser);
  };

  const logout = async () => {
    try {
      // POST /auth/logout — backend clears the httpOnly cookie via delete_cookie()
      await authAPI.logout();
    } catch {
      // Still clear local state even if request fails
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('sub_status');
    localStorage.removeItem('user_role');
    localStorage.removeItem('onboarding_done');
    // refresh_token was never in localStorage — nothing to remove here.
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, setSession, loading, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
